/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import _ from 'lodash';
import can from 'can';
import youbora from 'youboralib';
import dayjs from 'dayjs';

import 'youbora-adapter-videojs';
import BaseWidget from 'scripts/base-widget';
import DomEventsManager from 'scripts/dom-events-manager';
import FeedService from 'scripts/services/base-feed-service';
import PlayerService from 'scripts/services/player-service';
import datetimeUtil from 'scripts/utils/date-time';
import appContext from 'scripts/app-context';
import deviceUtil from 'scripts/utils/device';
import liveVideoService from 'scripts/services/live-video-service';
import dtm from 'scripts/services/dtm';

import view from './leaderboard.stache';
import ViewModel from './leaderboard.model';
import drawerView from './leaderboard-drawer.stache';
import leaderboardLivePlayer from './live-video-player.stache';
import settings from 'scripts/utils/settings';

import { showLiveVideoModal } from 'components/live-video-modal/live-video-modal';
import { DRAWER_TYPES } from 'components/leaderboard/drawer/drawer';

const inWebView = window.globalSettings.inWebView;
const defaultTimezone = window.globalSettings.defaultTimezone;

const moduleSettings = settings.getModuleSettings('leaderBoard');
const isLeaderboardEnabled = moduleSettings.isEnabled;
const disabledMessage = moduleSettings.unavailabilityMessage;

const youboraOptions = _.get(window.globalSettings, 'youbora', {});

export const LeaderBoard = BaseWidget.extend({
    lazyLoadingDisabled: true,
    activeDrawer: null,

    init() {
        this.view = view;

        function showComparisonPill() {
            return (
                this.viewModel.attr('firstPlayerId') ||
                this.viewModel.attr('secondPlayerId')
            );
        }

        this.viewModel = new ViewModel({
            isEnabled: isLeaderboardEnabled,
            disabledMessage: disabledMessage,
            favoritesIds: PlayerService.getFavorites(),
            sponsor: this.options.sponsor,
            playoffScoring: null,
            firstPlayerId: null,
            secondPlayerId: null,
            onResetPlayer: this.onResetPlayer.bind(this),
            defaultTimezone,
            showComparisonPill: showComparisonPill.bind(this),
        });
        this.fetchData();
        this.fetchSponsor();

        this._super();
        DomEventsManager.subscribe(this);

        this.searchContainer = this.$element.find('.s-head-search');

        appContext.model.bind(
            'leaderboard-live-video-play',
            (event, identifier, playerFullName) => {
                this.showLivePlayer(
                    { video: { identifier: identifier } },
                    playerFullName,
                );
            },
        );
    },

    fetchSponsor() {
        let url = window.globalSettings.feeds.settings;
        this.feedService = new FeedService({ feedUrl: url, fromCache: true });
        this.feedService.fetch();
        this.feedService.on('data', (ev, data) => {
            if (can.$('html').hasClass('mobile')) {
                this.viewModel.attr(
                    'sponsor',
                    _.get(data, 'leaderboardSettings.mobileSponsor'),
                );
            } else {
                this.viewModel.attr(
                    'sponsor',
                    _.get(data, 'leaderboardSettings.sponsor'),
                );
            }
        });
    },

    isDisplayValueEmpty(item, prop) {
        let val = item[prop];
        if (!val) {
            return;
        }
        return ['0', '', '-', 'WD', 'DQ'].indexOf(val.displayValue) > -1;
    },

    fetchData() {
        let url = window.globalSettings.feeds.smt.leaderboardUrl;
        this.feedService = new FeedService({ feedUrl: url, fromCache: false });
        this.feedService.fetch();
        this.feedService
            .on('fetching', (ev, value) =>
                this.viewModel.attr('fetching', value),
            )
            .on('data', (ev, data) => {
                let standings = _.get(data, 'standings', []);

                const previousStandings = this.viewModel.attr('standings');
                const newStandingsMap = {};
                const previousStandingsMap = {};
                const removedIndexes = [];
                standings.forEach((standing) => {
                    standing.isCut =
                        ['WD', 'MC', 'DQ', 'DNS'].indexOf(
                            _.get(standing, 'position.displayValue'),
                        ) > -1;
                    newStandingsMap[standing.player.identifier] = standing;
                    if (standing.holesThroughDate) {
                        standing.holesThrough.dateValue = datetimeUtil.getTimeDiffFromNightInStr(
                            standing.holesThroughDate,
                        );
                        standing.holesThrough.displayDateValue = dayjs
                            .parseZone(standing.holesThroughDate)
                            .format('hh:mm A');

                        if (standing.annotation) {
                            standing.holesThrough.displayDateValue +=
                                standing.annotation;
                        }
                    }
                    standing.notEmptyThru = this.isDisplayValueEmpty(
                        standing,
                        'holesThrough',
                    )
                        ? 0
                        : 1;
                    standing.notEmptyRd0 = this.isDisplayValueEmpty(
                        standing,
                        'roundScores.0.score',
                    )
                        ? 0
                        : 1;
                    standing.notEmptyRd1 = this.isDisplayValueEmpty(
                        standing,
                        'roundScores.1.score',
                    )
                        ? 0
                        : 1;
                    standing.notEmptyRd2 = this.isDisplayValueEmpty(
                        standing,
                        'roundScores.2.score',
                    )
                        ? 0
                        : 1;
                    standing.notEmptyRd3 = this.isDisplayValueEmpty(
                        standing,
                        'roundScores.3.score',
                    )
                        ? 0
                        : 1;
                });

                can.batch.start();
                if (!previousStandings || !previousStandings.length) {
                    this.viewModel.attr('standings', standings);
                } else {
                    previousStandings.forEach((standing, index) => {
                        const playerId = _.get(standing, 'player.identifier');

                        if (newStandingsMap[playerId]) {
                            previousStandingsMap[playerId] = standing;
                        } else {
                            removedIndexes.push(index);
                        }
                    });

                    _.reduce(
                        removedIndexes,
                        (accum, index) => {
                            previousStandings.splice(index - accum, 1);
                            return accum + 1;
                        },
                        0,
                    );

                    const newStandings = new can.List(standings);
                    newStandings.each((standing) => {
                        const activeStanding =
                            previousStandingsMap[standing.player.identifier];
                        if (!activeStanding) {
                            previousStandings.push(standing);
                        } else {
                            standing.each((value, key) => {
                                activeStanding.attr(key, value);
                            });
                            activeStanding.each((value, key) => {
                                if (standing.attr(key) === undefined) {
                                    activeStanding.removeAttr(key);
                                }
                            });
                        }
                    });
                }
                if (_.get(data, 'extraPlayoff.players.length', 0) > 0) {
                    this.viewModel.attr('playoffScoring', data);
                } else {
                    this.viewModel.removeAttr('playoffScoring');
                }

                const legend = data.fullLegend || [];

                this.viewModel.attr('legend', legend.join(' | '));
                this.viewModel.attr(
                    'currentRound',
                    _.get(data, 'currentRound.number', 1),
                );
                this.viewModel.attr('cutLine', data.cutLine || {});
                this.viewModel.recalculateStandings();
                this.viewModel.updateSort();
                can.batch.stop();
                if (this.activeDrawer) {
                    const activeDrawerIdentifier = this.activeDrawer.attr(
                        'data-identifier',
                    );
                    const isFavoriteDrawer = !!this.activeDrawer.attr(
                        'data-is-favorite',
                    );
                    let playerRow = null;
                    if (isFavoriteDrawer) {
                        playerRow = this.element.find(
                            `.js-board-row[data-identifier="${activeDrawerIdentifier}"][data-is-favorite="true"]`,
                        );
                    } else {
                        playerRow = this.element.find(
                            `.js-board-row[data-identifier="${activeDrawerIdentifier}"][data-is-favorite!="true"]`,
                        );
                    }
                    if (!playerRow.length) {
                        this.removeDrawer();
                    } else {
                        if (
                            this.activeDrawer.prev().attr('data-identifier') !=
                            activeDrawerIdentifier
                        ) {
                            playerRow.after(this.activeDrawer);
                        }
                    }
                }
            });
    },

    isSelected(playerId) {
        const selection = [
            this.viewModel.attr('firstPlayerId'),
            this.viewModel.attr('secondPlayerId'),
        ];
        return selection.some((selectedId) => selectedId == playerId);
    },

    availableToSelect(data) {
        if (
            data.attr('position.value') >
            this.viewModel.attr('cutLine.position')
        ) {
            return false;
        }
        return (
            this.viewModel.attr('firstPlayerId') &&
            this.viewModel.attr('secondPlayerId')
        );
    },

    toggleDrawer($el) {
        let identifier = $el.data('identifier');
        let playerName = $el.data('name');
        let activeDraverIdentifier = null;
        if (this.activeDrawer) {
            activeDraverIdentifier = this.activeDrawer.attr('data-identifier');
            this.removeDrawer();
            $el.find('.js-cell-player-btn').attr('aria-expanded', false);
        } else {
            dtm.trackLeaderboardExpand(playerName);
        }
        if (activeDraverIdentifier != identifier) {
            $el.parent()
                .find('.js-cell-player-btn')
                .attr('aria-expanded', false);
            $el.find('.js-cell-player-btn').attr('aria-expanded', true);
            let data = this.viewModel.standings.filter(
                (item) => item.player.identifier == identifier,
            )[0];
            if (data) {
                $el.after(
                    drawerView({
                        data: data,
                        isSelected: this.isSelected(identifier),
                        disableSelectButton: this.availableToSelect(data),
                        onSelectPlayer: this.onSelectPlayer.bind(this),
                        removeDrawer: this.removeDrawer.bind(this),
                        type: DRAWER_TYPES.LEADERBOARD,
                    }),
                );
                this.activeDrawer = $el.next();
                if ($el.attr('data-is-favorite')) {
                    this.activeDrawer.attr('data-is-favorite', true);
                }
            }
        }
    },

    getActiveDrawerViewModel() {
        return this.activeDrawer.find('leaderboard-drawer').viewModel();
    },

    setDrawerSelection(playerId, value) {
        const activeDrawerPlayerId = this.activeDrawer.attr('data-identifier');

        if (activeDrawerPlayerId === playerId) {
            this.getActiveDrawerViewModel().setSelected(value);
        }
    },

    enableDrawerSelection() {
        this.getActiveDrawerViewModel().enableSelectButton();
    },

    handleSelectPlayer(playerId) {
        if (!this.viewModel.attr('firstPlayerId')) {
            this.viewModel.attr('firstPlayerId', playerId);
        } else if (!this.viewModel.attr('secondPlayerId')) {
            this.viewModel.attr('secondPlayerId', playerId);
        }

        this.setDrawerSelection(playerId, true);
    },

    handleResetPlayer(playerId) {
        if (this.viewModel.attr('firstPlayerId') === playerId) {
            this.viewModel.attr('firstPlayerId', null);
        } else if (this.viewModel.attr('secondPlayerId') === playerId) {
            this.viewModel.attr('secondPlayerId', null);
        }

        this.setDrawerSelection(playerId, false);
        this.enableDrawerSelection();
    },

    onSelectPlayer(playerId) {
        const isSelected = this.isSelected(playerId);

        if (isSelected) {
            this.handleResetPlayer(playerId);
        } else {
            this.handleSelectPlayer(playerId);
        }
    },

    onResetPlayer(context, element, type, playerId) {
        this.handleResetPlayer(playerId);
    },

    removeDrawer() {
        if (this.activeDrawer) {
            this.activeDrawer.remove();
            this.activeDrawer = null;
        }
    },

    showLivePlayer(videoData, title) {
        this.leaderboardLivePlayer = leaderboardLivePlayer({
            liveVideo: videoData,
            title,
            closeLiveVideoPlayer: () => {
                $('.s-leaderboard-content live-player').remove();
            },
        });

        this.element
            .find('.s-leaderboard-content')
            .append(this.leaderboardLivePlayer);
    },

    // EVENTS
    '.s-head-search__icon-search click': function() {
        if (!this.searchContainer.hasClass('is-expanded')) {
            this.searchContainer.find('.s-head-search__input').val('');
        }

        this.searchContainer.addClass('is-expanded');
    },

    '.cell-player .icon-video click'(element, event) {
        event.preventDefault();

        const identifier = element.data('video-identifier');

        if (deviceUtil.isMobileWeb) {
            showLiveVideoModal();
        } else if (deviceUtil.isMobile && !inWebView) {
            const playerName = element.parents('.s-board-row').data('name');

            this.showLivePlayer(
                { video: { identifier: identifier } },
                playerName,
            );
        } else {
            liveVideoService.openTab(identifier);
        }
    },

    // favorites
    '.icon-favorite-star click'(element, event) {
        event.stopPropagation();
        let playerId = element.data('identifier');
        let playerName = element.data('name');
        let closestRow = element.closest('tr');
        PlayerService.toggleFavorite(playerId.toString());
        if (
            !closestRow.hasClass('is-favorite') &&
            !closestRow.hasClass('is-user-favorite')
        ) {
            dtm.trackLeaderboardFavoriting(playerName);
        }
        // remove active favorite drawer
        if (this.activeDrawer) {
            const activeDrawerIdentifier = this.activeDrawer.attr(
                'data-identifier',
            );
            const isFavoriteDrawer = !!this.activeDrawer.attr(
                'data-is-favorite',
            );
            if (isFavoriteDrawer && activeDrawerIdentifier == playerId) {
                this.removeDrawer();
            }
        }
        this.viewModel.recalculateStandings();
    },

    '.js-board-row click'(element, event) {
        if (event.target.classList.contains('icon-video')) {
            return;
        }
        this.toggleDrawer(element);
    },

    '.js-cell-checkbox-btn click'(element, event) {
        const isKeyPressed = event.originalEvent.detail === 0;
        if (isKeyPressed) {
            element.find('.s-table-checkbox').click();
        }
    },
});
